import { ChangeDetectionStrategy, Component, OnInit, ViewChild, Injector } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatTabGroup } from '@angular/material/tabs';
import { ComponentStore } from '@ngrx/component-store';
import { Store } from '@ngrx/store';
import { PermissionFacade } from '../../../../../modules/permission/facades/permission.facade';
import { ViewColumnComponent } from '../../../../../view-column.component';
import { AppConfig } from '../../../../../visa';
import { SubSink } from 'subsink2';
import { PermissionStates } from '../../../permission-controller/root';
import { RolePermActions } from '../../../role-permissions-controller/actions';
import { RolePermSelectors } from '../../../role-permissions-controller/selectors';
import { RolePermStates } from '../../../role-permissions-controller/states';
import { TranslateService } from "@ngx-translate/core";

interface LocalState {
  deactivateReturn: boolean;
  selectedIndex: number;
  deactivateAdd: boolean;
  deactivateList: boolean;
}

@Component({
  selector: 'app-role-permission-edit',
  templateUrl: './role-permission-edit.component.html',
  styleUrls: ['./role-permission-edit.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [ComponentStore]

})
export class RolePermissionEditComponent extends ViewColumnComponent {

  @ViewChild(MatTabGroup, {static: true}) matTab: MatTabGroup;

  protected readonly index = 2;
  private localState: LocalState;

  readonly localState$ = this.viewColFacade.selectLocalState(this.index);
  readonly deactivateReturn$ = this.componentStore.select(state => state.localState.deactivateReturn);
  readonly selectedIndex$ = this.componentStore.select(state => (state.localState.selectedIndex));

  prevIndex: number;
  private prevLocalState: any;

  apiVisa = AppConfig.apiVisa;

  private subSink = new SubSink;

  public form: FormGroup;

  // keep track of tabs
  currentIndexTab: number;
  // keep track of form being enabled/disabled
  formDisabled: boolean = false;

  // selected role guid
  roleGuid: string;

  //translation variable
  title: string;
  back_button: string;
  save_button: string;
  details_text: string;
  user_text: string;
  permission_set_text: string;
  client_side_permission_text: string;
  pricing_scheme_link_text: string;
  copy_permission_text: string;

  constructor(
    private fb: FormBuilder,
    private viewColFacade: PermissionFacade,
    private readonly store: Store<PermissionStates>,
    private readonly componentStore: ComponentStore<{localState: LocalState}>,
    private injector: Injector
  ) {
    super();
    const translateService = this.injector.get(TranslateService, null);
    if (translateService) {
      translateService.get('labels').subscribe(res => {
        this.title = res.edit_role_permissions;
        this.back_button = res.back_button;
        this.save_button = res.save_button;
        this.details_text = res.details;
        this.user_text = res.user;
        this.permission_set_text = res.permission_set;
        this.client_side_permission_text = res.client_side_permission;
        this.pricing_scheme_link_text = res.pricing_scheme_link;
        this.copy_permission_text = res.copy_permission;
      });
    } else {
      this.title = 'Edit Role Permissions';
      this.back_button = 'Back';
      this.save_button = 'SAVE';
      this.details_text = 'Details';
      this.user_text = 'User';
      this.permission_set_text = 'Permission Set';
      this.client_side_permission_text = 'Client Side Permission';
      this.pricing_scheme_link_text = 'Pricing Scheme Link';
      this.copy_permission_text = 'Copy Permission';
    }
  }

  /**
   * Lifecycle method: Called when component is created.
   */
  ngOnInit(): void {
    this.subSink.sink = this.localState$.subscribe(a => {
      this.localState = a;
      this.componentStore.setState({localState: a});

      // get currently selected index and disable form if necessary
      this.currentIndexTab = a.selectedIndex;
      this.formDisabled = this.currentIndexTab == 0 ? false : true;
    });

    this.subSink.sink = this.viewColFacade.prevIndex$.subscribe(resolve => this.prevIndex = resolve);
    this.subSink.sink = this.viewColFacade.prevLocalState$().subscribe(resolve => this.prevLocalState = resolve);
    this.subSink.sink = this.store.select(RolePermSelectors.selectGuid).subscribe(guid => {
      if (!guid) {
        this.onReturn();
      } else {
        this.roleGuid = guid
      }
    })

    // initialise a new form
    this.form = this.fb.group({
      detailsForm: this.fb.group({
        name: ['', Validators.compose([Validators.required])],
        description: [''],
        currStatus: [''],
        status: [''],

        // uneditable
        code: [''],
        createdDate: [''],
        updatedDate: [''],
        createdBy: [''],
        updatedBy: ['']
      })
    });

    // save details form in ngrx store
    this.store.dispatch(RolePermActions.setDetailsForm({form: this.form.get('detailsForm').value}));

    // enable/disable form based on current index change
    this.subSink.sink = this.matTab.selectedIndexChange.subscribe(x =>
      {
        this.currentIndexTab = x;
        this.formDisabled = this.currentIndexTab == 0 ? false : true;
      })

  }

  /**
   * Update role details.
   */
  onSubmit() {
    this.store.dispatch(RolePermActions.updateRoleInit({guid: this.roleGuid, role: this.form.get('detailsForm').value}))
  }

  /**
  Return to previous drawer.
  */
  onReturn() {
    this.viewColFacade.updateInstance(this.prevIndex, {
      ...this.prevLocalState,
      deactivateAdd: false,
      deactivateList: false
    })
    this.viewColFacade.onPrev(this.prevIndex);
  }


  /**
   * Lifecycle method: Called when component is destroyed.
   */
  ngOnDestroy() {
    if (this.matTab) {
      this.viewColFacade.updateInstance(this.index, {
        ...this.localState,
        selectedIndex: this.matTab.selectedIndex
      });
    }
    this.subSink.unsubscribe();
  }

}
